import { useState, useMemo, useCallback, type ReactNode, SetStateAction, Dispatch } from "react";
import {
  useTellescopeInfiniteQuery,
  TellescopeModelFromName,
  type UseTellescopeInfiniteQueryOptions,
  type ParamsForMethod,
} from "@tellescope/tanstack-query";
import {
  Combobox,
  ComboboxOption,
  ComboboxOptionRenderProps,
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@tellescope/ui";
import { useDebouncedValue } from "@tellescope/react-hooks";
import { formatName, formatInitials, cn } from "@tellescope/utils";
import { Check } from "lucide-react";
import { i18n } from "@tellescope/i18n";

/**
 * Type representing an user model from the Tellescope API
 */
export type User = TellescopeModelFromName<"users">;

/**
 * Props for the default user option renderer
 */
interface DefaultUserOptionProps {
  user: User;
  isSelected: boolean;
}

/**
 * Default option renderer component with three variants
 */
function DefaultUserOption({ user, isSelected }: DefaultUserOptionProps): ReactNode {
  return (
    <>
      <div className="flex gap-2 items-center w-full">
        <Avatar className="h-8 w-8">
          <AvatarImage src={user.avatar} />
          <AvatarFallback useStaticBackground={true} id={user.id}>
            {formatInitials(user)}
          </AvatarFallback>
        </Avatar>
        <div className="flex flex-col min-w-0 flex-1">
          <p className="font-semibold text-sm truncate">{formatName(user)}</p>
        </div>
      </div>
      <Check className={cn("ml-2 h-4 w-4 shrink-0", isSelected ? "opacity-100" : "opacity-0")} />
    </>
  );
}

/**
 * Props for the UserSearchCombobox component
 */
export interface UserSearchComboboxProps {
  // ===== Selection (Controlled State) =====
  /** Current selected user(s) - MUST be controlled externally */
  value: User[];

  /** Callback when selection changes */
  onChange: (value: User[]) => void;

  /** Enable multi-select mode */
  multiple?: boolean;

  // ===== Search Configuration =====
  /** Debounce delay for API search in milliseconds (default: 500) */
  debounceMs?: number;

  // ===== Query Configuration =====
  /**
   * Optional additional TanStack Query options.
   * The `model`, `method`, `request.search`, and `request.limit` are managed internally.
   * You can provide additional request params (e.g., filters), or override `enabled`, `staleTime`, etc.
   */
  queryOptions?: Partial<
    Omit<
      UseTellescopeInfiniteQueryOptions<"users", "getSome", string | undefined, Error>,
      "model" | "method" | "getNextPageParam" | "initialPageParam" | "paginateRequest" | "select"
    >
  > & {
    /** Additional request parameters to merge with the search query */
    requestOverrides?: Partial<ParamsForMethod<"users", "getSome">>;
  };

  /** Custom option renderer - receives user data via metadata */
  formatOption?: (props: ComboboxOptionRenderProps<User>) => ReactNode;

  /** Custom selected value formatter */
  formatSelectedValue?: (value: User[]) => ReactNode;

  /** Placeholder text for trigger button (default: "Select user...") */
  placeholder?: string;

  /** Placeholder text for search input (default: "Search by name, email, or phone...") */
  searchPlaceholder?: string;

  /** Text shown while loading results (default: "Searching...") */
  loadingText?: string;

  /** Text shown when no results found (default: "No users found") */
  emptyText?: string;

  /** Show clear button (default: true) */
  clearable?: boolean;

  /** CSS class for trigger button */
  className?: string;

  /** CSS class for popover content */
  popoverClassName?: string;

  /** Accessibility label */
  "aria-label"?: string;

  /** Custom filter function for client-side filtering of options */
  customFilter?: (searchTerm: string, options: ComboboxOption<User>[]) => ComboboxOption<User>[];

  children?: ReactNode;

  alwaysFilter?: boolean;

  defaultOpen?: boolean;
  open?: boolean;
  setOpen?: Dispatch<SetStateAction<boolean>>;
}

/**
 * Reusable user search combobox component with debounced API search.
 *
 * This component wraps the base Combobox with user-specific functionality:
 * - Automatic TanStack Query integration with debounced search
 * - Controlled user selection (single or multi-select)
 * - Three default option renderer variants (default, phone, email)
 * - Customizable query options and rendering
 *
 * @example
 * // Simple single-select
 * const [selectedUser, setSelectedUser] = useState<User>();
 * <UserSearchCombobox
 *   value={selectedUser}
 *   onChange={setSelectedUser}
 * />
 *
 * @example
 * // Multi-select with phone display
 * const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
 * <UserSearchCombobox
 *   multiple
 *   value={selectedUsers}
 *   onChange={setSelectedUsers}
 *   defaultRendererVariant="phone"
 * />
 *
 * @example
 * // With filters and custom options
 * <UserSearchCombobox
 *   value={selectedUser}
 *   onChange={setSelectedUser}
 *   queryOptions={{
 *     requestOverrides: {
 *       filter: { tags: ["vip"] }
 *     },
 *     staleTime: 30000
 *   }}
 * />
 *
 * @example
 * // Custom rendering
 * <UserSearchCombobox
 *   value={selectedUser}
 *   onChange={setSelectedUser}
 *   formatOption={(props) => <CustomUserItem {...props} />}
 * />
 */
export function UserSearchCombobox({
  value,
  onChange,
  multiple,
  debounceMs = 500,
  queryOptions,
  formatOption,
  formatSelectedValue,
  placeholder = i18n.t("Select user..."),
  searchPlaceholder = i18n.t("Search by name, email, or phone..."),
  loadingText = i18n.t("Searching..."),
  emptyText = i18n.t("No users found"),
  clearable = true,
  className,
  popoverClassName,
  "aria-label": ariaLabel,
  customFilter,
  children,
  alwaysFilter,
  defaultOpen = false,
  open,
  setOpen,
}: UserSearchComboboxProps) {
  // Internal search state with debouncing
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery] = useDebouncedValue(searchQuery, debounceMs);

  // Query users with debounced search
  const usersQuery = useTellescopeInfiniteQuery({
    model: "users",
    method: "getSome",
    request: {
      limit: 20,
      search: debouncedSearchQuery ? { query: debouncedSearchQuery } : undefined,
      ...queryOptions?.requestOverrides,
    },
    select: (data) => data.pages.flat(),
    ...queryOptions,
  });

  // Map users to combobox options
  const comboboxOptions: ComboboxOption<User>[] = useMemo(
    () =>
      usersQuery.data?.map((user) => ({
        value: user.id,
        label: formatName(user),
        metadata: { user },
      })) ?? [],
    [usersQuery.data]
  );

  // Convert external value to combobox format (always array for internal Combobox)
  const comboboxValue: ComboboxOption<User>[] = useMemo(() => {
    const arrayValue = value;
    return arrayValue.map((user) => ({
      value: user.id,
      label: formatName(user),
      metadata: { user },
    }));
  }, [value]);

  // Handle selection changes - convert array back to single value if needed
  const handleChange = useCallback(
    (options: ComboboxOption<User>[]) => {
      const users = options.map((opt) => opt.metadata?.user).filter((e): e is User => !!e);

      if (multiple) {
        onChange(users);
        return;
      }

      const currentIds = value.map(({ id }) => id);
      const newVal = users.filter(({ id }) => !currentIds.includes(id));
      onChange(newVal.slice(0, 1));
    },
    [multiple, onChange, value]
  );

  // Default option renderer
  const defaultFormatOption = useMemo(
    () => (props: ComboboxOptionRenderProps<User>) => {
      const user = props.option.metadata?.user;
      if (!user) return null;
      return <DefaultUserOption user={user} isSelected={props.isSelected} />;
    },
    []
  );

  // Wrap custom formatSelectedValue to convert ComboboxOption to User
  const wrappedFormatSelectedValue = useMemo(() => {
    if (!formatSelectedValue) return undefined;

    return (val: ComboboxOption<User>[]) => {
      const users = val.map((opt) => opt.metadata?.user).filter((e): e is User => !!e);
      return formatSelectedValue(users);
    };
  }, [formatSelectedValue]);

  return (
    <Combobox
      // Selection
      value={comboboxValue}
      onChange={handleChange}
      multiple
      // Options
      options={comboboxOptions}
      alwaysFilter={alwaysFilter}
      customFilter={customFilter}
      // Search
      searchQuery={searchQuery}
      setSearchQuery={setSearchQuery}
      searchPlaceholder={searchPlaceholder}
      // Rendering
      formatOption={formatOption ?? defaultFormatOption}
      formatSelectedValue={wrappedFormatSelectedValue}
      // Loading states
      isLoading={usersQuery.isLoading || searchQuery !== debouncedSearchQuery}
      loadingText={loadingText}
      emptyText={emptyText}
      // UI
      placeholder={placeholder}
      clearable={clearable}
      className={className}
      popoverClassName={popoverClassName}
      aria-label={ariaLabel}
      children={children}
      defaultOpen={defaultOpen}
      open={open}
      setOpen={setOpen}
    />
  );
}
